/**
 * Pure detection logic for plan/quota-restriction API errors — deliberately
 * has NO dependency on the Redux store, so api.service.ts (imported by
 * nearly every service, including ones the store's own slices pull in) can
 * import this without creating a circular module chain
 * (api.service -> store -> a slice -> a *.service.ts -> api.service).
 *
 * See plan-lock.util.ts for the store-dispatching half of this, which is
 * imported by React components/hooks and call-site catch blocks instead.
 */

const PLAN_FEATURE_MESSAGE_MARKER = 'is not available on your current plan';

export interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: unknown;
}

export interface PlanLockPayload {
  title: string;
  message: string;
  /** 'wallet' = blocked by the prepaid wallet (top up), not by the plan (upgrade). */
  kind?: 'plan' | 'wallet';
}

/**
 * Prepaid-wallet refusals: a WhatsApp send the wallet can't cover
 * (WALLET_INSUFFICIENT_BALANCE), or an AI call blocked by an empty wallet —
 * that one reuses QUOTA_EXCEEDED (so backend channels degrade the same way)
 * and is told apart by `details.wallet === true`.
 */
export function isWalletLockError(apiError?: ApiErrorBody): boolean {
  if (!apiError) return false;
  if (apiError.code === 'WALLET_INSUFFICIENT_BALANCE') return true;
  return apiError.code === 'QUOTA_EXCEEDED' && (apiError.details as { wallet?: unknown } | undefined)?.wallet === true;
}

export function buildWalletLockPayload(message?: string): PlanLockPayload {
  return {
    kind: 'wallet',
    title: 'Wallet balance too low',
    message: message || 'Your prepaid wallet does not have enough balance for this. Top up to continue.',
  };
}

/** Wallet first (it shares QUOTA_EXCEEDED), then plan. Null when neither. */
export function lockPayloadFor(apiError?: ApiErrorBody): PlanLockPayload | null {
  if (!apiError) return null;
  if (isWalletLockError(apiError)) return buildWalletLockPayload(apiError.message);
  if (isPlanLockError(apiError.code, apiError.message)) return buildPlanLockPayload(apiError.code!, apiError.message!);
  return null;
}

/**
 * FORBIDDEN is also thrown from ~14 unrelated places in the backend (RBAC
 * denials, suspended-tenant checks, protected system roles, etc). Only
 * requireFeature()'s message contains this exact phrase, so the substring
 * check is what keeps this from firing on unrelated 403s. QUOTA_EXCEEDED has
 * no other source — always a plan-lock.
 */
export function isPlanLockError(code?: string, message?: string): boolean {
  if (code === 'QUOTA_EXCEEDED') return true;
  return code === 'FORBIDDEN' && !!message?.includes(PLAN_FEATURE_MESSAGE_MARKER);
}

export function buildPlanLockPayload(code: string, message: string): PlanLockPayload {
  return {
    title: code === 'QUOTA_EXCEEDED' ? 'Usage limit reached' : 'Feature locked',
    message,
  };
}

/** Called from the axios response interceptor — tags the error, does not dispatch. */
export function tagAxiosErrorWithPlanLock(error: any): void {
  const payload = lockPayloadFor(error?.response?.data?.error as ApiErrorBody | undefined);
  if (payload) error.planLock = payload;
}
